import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import BreadcrumbNavigation from '../../components/ui/BreadcrumbNavigation';
import Button from '../../components/ui/Button';
import SalonCard from './components/SalonCard';
import SummaryMetrics from './components/SummaryMetrics';
import AddSalonModal from './components/AddSalonModal';
import DeleteConfirmationModal from './components/DeleteConfirmationModal';
import SearchAndFilter from './components/SearchAndFilter';
import Icon from '../../components/AppIcon';
import api from 'utils/api';

const SalonDashboard = () => {
  const navigate = useNavigate();
  const isEmployee = (() => { try { return JSON.parse(atob(localStorage.getItem('token')?.split('.')[1] || ''))?.role === 'employee'; } catch { return false; } })();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedSalon, setSelectedSalon] = useState(null);
  const [filteredSalons, setFilteredSalons] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeFilter, setActiveFilter] = useState('all');
  const [loading, setLoading] = useState(false);

  // Mock data for salons
  const [salons, setSalons] = useState([]);

  const fetchSalons = async () => {
    setLoading(true);
    try {
      const res = await api.get('/salons');
      const salonList = Array.isArray(res.data) ? res.data : [];
      const enrichedSalons = await Promise.all(salonList.map(async (salon) => {
        const salonId = salon?._id;
        if (!salonId) return salon;
        try {
          const [employeesRes, servicesRes] = await Promise.all([
            api.get(`/salons/${salonId}/employees`),
            api.get(`/salons/${salonId}/services`),
          ]);
          const employees = Array.isArray(employeesRes.data) ? employeesRes.data : [];
          const services = Array.isArray(servicesRes.data) ? servicesRes.data : [];
          return {
            ...salon,
            employeeCount: employees.filter((employee) => employee.status !== 'inactive').length,
            serviceCount: services.length,
          };
        } catch {
          return { ...salon, employeeCount: 0, serviceCount: 0 };
        }
      }));
      setSalons(enrichedSalons);
      localStorage.setItem('@salons', JSON.stringify(enrichedSalons));
      console.log(res.data);
    } catch (err) {
      console.error('Error fetching salons:', err);
      alert('خطا در دریافت اطلاعات سالن‌ها');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSalons();
  }, []);

  // Summary metrics
  const summaryMetrics = {
    totalSalons: salons?.length,
    totalEmployees: salons?.reduce(
      (sum, salon) => sum + salon?.employeeCount,
      0,
    ),
    totalServices: salons?.reduce((sum, salon) => sum + salon?.serviceCount, 0),
    todayAppointments: salons?.reduce(
      (sum, salon) => sum + salon?.todayAppointments,
      0,
    ),
  };

  // Filter salons based on search and filter
  useEffect(() => {
    let filtered = salons;

    // Apply search filter
    if (searchTerm) {
      filtered = filtered?.filter(
        (salon) =>
          salon?.name?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
          salon?.address?.toLowerCase()?.includes(searchTerm?.toLowerCase()),
      );
    }

    // Apply status filter
    if (activeFilter !== 'all') {
      filtered = filtered?.filter((salon) => salon?.status === activeFilter);
    }

    setFilteredSalons(filtered);
  }, [salons, searchTerm, activeFilter]);

  const handleToggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const handleCloseSidebar = () => {
    setSidebarOpen(false);
  };

  const handleAddSalon = async (newSalonData) => {
    console.log('before save', newSalonData);
    try {
      const res = await api.post('/salons', newSalonData);
      const newSalon = res.data;
      console.log(newSalon);
      // Optional: add computed props for UI
      setSalons((prev) => [
        ...prev,
        {
          ...newSalon,
          status: 'active',
          employeeCount: 0,
          serviceCount: 0,
          todayAppointments: 0,
        },
      ]);
    } catch (err) {
      console.error('Error creating salon:', err);
      alert('خطا در ایجاد سالن');
    }
  };

  const handleEditSalon = (salon) => {
    navigate('/salon-management', { state: { selectedSalon: salon } });
  };

  const handleDeleteSalon = (salon) => {
    setSelectedSalon(salon);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = (salon) => {
    setSalons((prev) => prev?.filter((s) => s?.id !== salon?.id));
    setSelectedSalon(null);
  };

  const handleViewDetails = (salon) => {
    navigate('/salon-management', { state: { selectedSalon: salon } });
  };

  const handleSearch = (term) => {
    setSearchTerm(term);
  };

  const handleFilter = (filter) => {
    setActiveFilter(filter);
  };

  return (
    <div className="min-h-screen bg-background" dir="rtl">
      <Sidebar isOpen={sidebarOpen} onClose={handleCloseSidebar} />
      {/* <Header
        isCollapsed={!sidebarOpen}
        onToggleSidebar={handleToggleSidebar}
      /> */}
      <main className="lg:mr-60 pt-16">
        <div className="p-6">
          {!isEmployee && <div className="mb-6 grid grid-cols-1 gap-3 sm:grid-cols-3">
            {[
              ['رزروها', '/booking-management', 'CalendarCheck'],
              ['کارمندان', '/employee-management', 'Users'],
              ['خدمات', '/service-management', 'Scissors'],
            ].map(([label, path, icon]) => (
              <button key={path} onClick={() => navigate(path)} className="flex items-center gap-3 rounded-2xl border border-border bg-card p-4 text-right shadow-sm transition hover:-translate-y-0.5 hover:border-primary">
                <span className="rounded-xl bg-primary/10 p-3 text-primary"><Icon name={icon} size={22} /></span>
                <span className="font-semibold text-foreground">مدیریت {label}</span>
                <Icon name="ChevronLeft" size={18} className="mr-auto text-muted-foreground" />
              </button>
            ))}
          </div>}
          {/* Page Header */}
          <div className="mb-6">
            <BreadcrumbNavigation />
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mt-4">
              <div>
                <h1 className="text-2xl font-bold text-foreground">
                  داشبورد سالن‌ها
                </h1>
                <p className="text-muted-foreground mt-1">
                  مدیریت و نظارت بر تمام سالن‌های زیبایی شما
                </p>
              </div>
              <Button
                disabled
                variant="default"
                onClick={() => setShowAddModal(true)}
                iconName="Plus"
                iconPosition="right"
                className="w-full sm:w-auto">
                افزودن سالن جدید
              </Button>
            </div>
          </div>

          {/* Summary Metrics */}
          <SummaryMetrics metrics={summaryMetrics} />

          {/* Search and Filter */}
          {/* <SearchAndFilter
            onSearch={handleSearch}
            onFilter={handleFilter}
            totalCount={filteredSalons?.length}
          /> */}

          {/* Salons Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredSalons?.map((salon) => (
              <SalonCard
                key={salon?._id ?? salon?.id}
                salon={salon}
                onEdit={handleEditSalon}
                onDelete={handleDeleteSalon}
                onViewDetails={handleViewDetails}
              />
            ))}
          </div>

          {/* Empty State */}
          {filteredSalons?.length === 0 && (
            <div className="text-center py-12">
              <div className="w-24 h-24 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                <Icon
                  name="Store"
                  size={32}
                  className="text-muted-foreground"
                />
              </div>
              <h3 className="text-lg font-medium text-foreground mb-2">
                {searchTerm || activeFilter !== 'all'
                  ? 'سالنی یافت نشد'
                  : 'هنوز سالنی اضافه نکرده‌اید'}
              </h3>
              <p className="text-muted-foreground mb-6">
                {searchTerm || activeFilter !== 'all'
                  ? 'لطفاً جستجو یا فیلتر خود را تغییر دهید'
                  : 'اولین سالن خود را اضافه کنید تا شروع کنید'}
              </p>
              {!searchTerm && activeFilter === 'all' && (
                <Button
                  disabled
                  variant="default"
                  onClick={() => setShowAddModal(true)}
                  iconName="Plus"
                  iconPosition="right">
                  افزودن سالن جدید
                </Button>
              )}
            </div>
          )}
        </div>
      </main>
      {/* Modals */}
      <AddSalonModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSave={handleAddSalon}
        ownerId={'66e78c12fa3a5f4bcd92ab11'}
      />
      <DeleteConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleConfirmDelete}
        salon={selectedSalon}
      />
    </div>
  );
};

export default SalonDashboard;
